import type { Asset } from '../../../types/asset.types';
import type {
  ImageAssetType,
  ImageModelCaches
} from '@/components/ui/carousel/carousel.types';

/**
 * Grid `ImageAssetType` → wire `Asset`, for the optimistic trash placeholder
 * (DV-2524 §13). The inverse of `model/toImageAsset`, but narrower on purpose:
 * it exists so a just-deleted photo can appear in the cached trash listing
 * before the server confirms, which is what makes the count badge and the trash
 * grid move instantly.
 *
 * Fields `TrashCard` never reads (`type`, `url`, `updatedAt`, the sync flags)
 * take honest defaults rather than invented values — the reconciling refetch
 * replaces this object with the real one moments later.
 */
export function toTrashedAsset(
  image: ImageAssetType,
  photosetUUID: string
): Asset {
  return {
    assetUUID: image.assetUUID,
    photoUUID: image.photoUUID,
    orderUUID: image.orderUUID,
    propertyUUID: image.propertyUUID,
    sortOrder: image.sortOrder ?? 0,
    caption: image.caption ?? null,
    type: 'photo',
    url: null,
    caches: definedCaches(image.caches),
    updatedAt: null,
    isSyncedNonWatermarked: null,
    isSyncedWatermarked: null,
    hidden: image.hidden,
    staged: image.staged,
    // The card's expiry pill derives from this, so it reads
    // "Expires <today + TRASH_RETENTION_DAYS>" with no server round trip.
    // Milliseconds: parseTrashTimestamp routes anything past MILLIS_THRESHOLD
    // through unchanged, so no epoch-seconds pretence is needed.
    trashedAt: Date.now(),
    trashedFromPhotosetUUID: photosetUUID
  };
}

/**
 * `ImageModelCaches` values are `string | undefined` (index signature);
 * `Asset['caches']` is `Record<string, string>`. Filtering rather than casting
 * keeps a present-but-undefined key from surviving into a shape that promises
 * every key resolves.
 */
function definedCaches(
  caches: ImageModelCaches | undefined
): Record<string, string> | null {
  if (!caches) return null;
  const defined: Record<string, string> = {};
  Object.entries(caches).forEach(([size, url]) => {
    if (url) defined[size] = url;
  });
  return defined;
}
